import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to={user.role === "client" ? "/portal" : "/"} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email, password);
    setSubmitting(false);
    if (result.success) {
      navigate(result.user?.role === "client" ? "/portal" : "/");
    } else {
      setError(result.error || "Invalid email or password");
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-[#1C1C1E] flex-col justify-end p-12 lg:p-16">
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/b49b5f10-3087-4d0f-adce-b804118659f8/images/9497a2851136b4ee621aafd285efe936f220ab69d5d59c477b40f0e1b7686213.png"
            alt="EnviroWealth — enterprise sustainability, ESG data and carbon analytics"
            className="w-full h-full object-cover motion-safe:animate-[heroZoom_18s_ease-out_forwards]"
            data-testid="login-hero-image"
          />
        </div>
        {/* Noise/grain texture overlay for a premium editorial feel */}
        <div
          className="absolute inset-0 opacity-[0.12] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
        {/* Dark gradient for text contrast */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1E] via-[#1C1C1E]/80 to-[#00823B]/25" />

        {/* Floating data chips */}
        <div
          className="absolute top-[14%] right-[10%] z-10 backdrop-blur-xl bg-white/10 border border-white/20 rounded-lg px-3.5 py-2 motion-safe:animate-[chipFloat_7s_ease-in-out_infinite]"
          data-testid="login-hero-chip-esg"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-white/70">Live</p>
          <p className="text-sm font-semibold text-white">142 Active ESG Engagements</p>
        </div>
        <div
          className="absolute top-[38%] left-[8%] z-10 backdrop-blur-xl bg-white/10 border border-white/20 rounded-lg px-3.5 py-2 motion-safe:animate-[chipFloat_8s_ease-in-out_infinite] [animation-delay:1.2s]"
          data-testid="login-hero-chip-scope3"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-[#76C121]">Analytics</p>
          <p className="text-sm font-semibold text-white">Scope 3 Carbon Tracking</p>
        </div>
        <div
          className="absolute top-[62%] right-[14%] z-10 backdrop-blur-xl bg-white/10 border border-white/20 rounded-lg px-3.5 py-2 motion-safe:animate-[chipFloat_6.5s_ease-in-out_infinite] [animation-delay:2.4s]"
          data-testid="login-hero-chip-renewal"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-white/70">On Track</p>
          <p className="text-sm font-semibold text-white">98% Client Renewal Rate</p>
        </div>

        <div className="relative z-20 max-w-lg motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-6 duration-1000">
          <img
            src="https://customer-assets.emergentagent.com/job_b49b5f10-3087-4d0f-adce-b804118659f8/artifacts/bpbmqyb1_ENVIROWEALTH%20LOGO%20%282%29.jpg"
            alt="EnviroWealth"
            className="h-8 object-contain mb-6 rounded-sm bg-white/95 px-2 py-1 inline-block"
          />
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight">
            Orchestrating enterprise sustainability.
          </h1>
          <p className="text-white/80 text-sm mt-4 tracking-wide leading-relaxed">
            Seamlessly manage ESG engagements, cross-functional teams, and carbon data in one unified workspace.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 sm:px-16">
        <div className="w-full max-w-sm" data-testid="login-page">
          <img
            src="https://customer-assets.emergentagent.com/job_b49b5f10-3087-4d0f-adce-b804118659f8/artifacts/bpbmqyb1_ENVIROWEALTH%20LOGO%20%282%29.jpg"
            alt="EnviroWealth"
            className="h-9 mb-8 object-contain lg:hidden"
          />
          <h1 className="font-heading text-2xl font-bold text-[#1C1C1E] tracking-tight mb-1">Welcome back</h1>
          <p className="text-sm text-[#4B5563] mb-8">Sign in to access your EnviroWealth workspace.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@envirowealth.com"
                className="focus:border-[#76C121] focus:ring-1 focus:ring-[#76C121]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="focus:border-[#76C121] focus:ring-1 focus:ring-[#76C121]"
              />
            </div>
            {error && (
              <p data-testid="login-error-message" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="w-full bg-[#76C121] hover:bg-[#5C9819] text-white font-medium"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
